import React from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { SwiperSlide } from 'swiper/react';

import { ChevronIcon, CouponFilledIcon } from '@icons';
import gtmTracker from '@utils/gtmTracker';

import { useQueryUserPromotions } from '@hooks/api/promotions/use-query-user-promotions';

import { Slider } from '@components/slider';

import * as Styled from './promotion-history.styled';
import { RecentPromotionCard } from './recent-promotion-card';

interface PaginationStepType {
	start: number;
	end: number;
}

const PromotionHistory = () => {
	const { t } = useTranslation('main');

	const [currentPage, setCurrentPage] = React.useState<number>(1);

	const { data: promos_list } = useQueryUserPromotions(6, currentPage);

	const [paginationStep, setPaginationStep] = React.useState<PaginationStepType>({
		start: 0,
		end: 5
	});

	const handleStepDecrease = () => {
		if (paginationStep.start <= 5) {
			setPaginationStep({
				start: 0,
				end: 5
			});
		} else {
			setPaginationStep({
				start: paginationStep.start - 6,
				end: paginationStep.start
			});
		}
	};

	const handleStepIncrease = () => {
		if (promos_list) {
			if (paginationStep.end >= promos_list.pagination.total_pages) {
				return;
			} else {
				setPaginationStep({
					start: paginationStep.end,
					end: paginationStep.end + 6
				});
			}
		}
	};

	const handleTrackEvent = () => {
		gtmTracker.trackEvent('click', {
			event_category: 'User_profile',
			event_action: 'Click',
			event_label: 'Pagination'
		});
	};

	const handlePageButtonClick = (page: number) => {
		handleTrackEvent();
		setCurrentPage(page);
	};

	const handleStepDecreaseTracked = () => {
		handleTrackEvent();
		handleStepDecrease();
	};

	const handleStepIncreaseTracked = () => {
		handleTrackEvent();
		handleStepIncrease();
	};

	return (
		<Styled.Wrapper>
			{promos_list && (
				<>
					{promos_list.lottery_cards.length > 0 && (
						<Styled.RecentPromotionsRow>
							<Slider
								freeMode
								spaceBetween='16px'
								slidesPerView='auto'
								grabCursor={true}
								navigation={false}
								pagination={false}
							>
								{promos_list.lottery_cards.map((promo, index) => (
									<SwiperSlide key={'recent' + index}>
										<RecentPromotionCard promo={promo} />
									</SwiperSlide>
								))}
							</Slider>
						</Styled.RecentPromotionsRow>
					)}
					<Styled.TableHeader>
						<Styled.TableHeaderItem>{t('promotion')}</Styled.TableHeaderItem>
						<Styled.TableHeaderItem>{t('tickets')}</Styled.TableHeaderItem>
						<Styled.TableHeaderItem>{t('date')}</Styled.TableHeaderItem>
						<Styled.TableHeaderItem>{t('status')}</Styled.TableHeaderItem>
					</Styled.TableHeader>
					<Styled.TableBody>
						{promos_list.lotteries.map((promo, index) => (
							<Styled.TableRow key={'list' + index}>
								<Styled.PromotionName>{promo.name}</Styled.PromotionName>
								<Styled.PromotionTicketCount>
									<Styled.TicketIcon>
										<CouponFilledIcon />
									</Styled.TicketIcon>
									{promo.tickets_count}
								</Styled.PromotionTicketCount>
								<Styled.PromotionEndDate>{dayjs(promo.end_date).format('DD.MM.YYYY')}</Styled.PromotionEndDate>
								<Styled.PromotionStatus>
									<Styled.StatusChip
										$status={
											promo.status === 'active'
												? Styled.ChipStatus.ACTIVE
												: promo.status === 'pending'
													? Styled.ChipStatus.PENDING
													: Styled.ChipStatus.ENDED
										}
									>
										{promo.status === 'active'
											? t('active_status')
											: promo.status === 'pending'
												? t('pending_status')
												: t('ended_status')}
									</Styled.StatusChip>
								</Styled.PromotionStatus>
							</Styled.TableRow>
						))}
						{promos_list.pagination.total_pages > 1 && (
							<Styled.TablePagination>
								{paginationStep.start !== 0 && (
									<Styled.TablePaginationButton onClick={handleStepDecreaseTracked} $isActive={false}>
										<ChevronIcon />
									</Styled.TablePaginationButton>
								)}
								{Array.from({ length: promos_list.pagination.total_pages })
									.slice(paginationStep.start, paginationStep.end)
									.map((_, i) => (
										<Styled.TablePaginationButton
											onClick={() => handlePageButtonClick(i + 1)}
											$isActive={i + 1 === currentPage}
											key={'pagination' + i}
										>
											{i + 1}
										</Styled.TablePaginationButton>
									))}
								{paginationStep.end < promos_list.pagination.total_pages && (
									<Styled.TablePaginationButton onClick={handleStepIncreaseTracked} $isActive={false}>
										<ChevronIcon.Right />
									</Styled.TablePaginationButton>
								)}
							</Styled.TablePagination>
						)}
					</Styled.TableBody>
				</>
			)}
		</Styled.Wrapper>
	);
};

export default PromotionHistory;
